const has = <T>(object: T, field: keyof T): boolean => object[field] !== undefined;

export type Leaf<T> = T;

export interface Joint<TLeaf, TJointData> {
    left: Node<TLeaf, TJointData>;
    right: Node<TLeaf, TJointData>;
    data?: TJointData;
}

export type Node<TLeaf, TJointData> = Leaf<TLeaf> | Joint<TLeaf, TJointData>;

export function isJoint<TLeaf, TJointData>(node: Node<TLeaf, TJointData>) {
    const hasLeft = has(node as Joint<TLeaf, TJointData>, 'left');
    const hasRight = has(node as Joint<TLeaf, TJointData>, 'right');
    return hasLeft || hasRight;
}

export function isLeaf<TLeaf, TJointData>(node: Node<TLeaf, TJointData>) {
    return !isJoint(node);
}

export function* enumerateNodes<TLeaf, TJointData>(
    root: Node<TLeaf, TJointData>,
): IterableIterator<Node<TLeaf, TJointData>> {
    const stack: Array<Node<TLeaf, TJointData>> = [];
    let current: Node<TLeaf, TJointData> | undefined = root;

    while (current !== undefined || stack.length > 0) {
        while (current !== undefined) {
            stack.push(current);
            current = (current as Joint<TLeaf, TJointData>).left;
        }

        current = stack.pop();
        yield current!;
        current = (current as Joint<TLeaf, TJointData>).right;
    }
}

export function* enumerateLeafs<TLeaf, TJointData>(root: Node<TLeaf, TJointData>): IterableIterator<Leaf<TLeaf>> {
    for (const node of enumerateNodes(root)) {
        if (isLeaf(node)) {
            yield node as Leaf<TLeaf>;
        }
    }
}
